import React, { useEffect, useState } from 'react';
import { Box, Breadcrumbs } from '@mui/material';
import { useIntl } from 'react-intl';
import { Home as HomeIcon } from '@mui/icons-material';
import { generatePath, Link } from 'react-router-dom';
import { FormContext, useForm } from 'react-hook-form';
import { useDispatch, useSelector } from 'react-redux';
import Bluebird from 'bluebird';
import {
  StyledRescuerBreadcrumb,
  StyledRescuerTextBreadcrumb,
} from '../../rescuerDashboard/rescuerDashboardMobile/Components/StyledRescuerBreadcrumb';
import routes from '../../../routes';
import useActiveUser from '../../../hooks/useActiveUser';
import { getUserRolesInCurrentlySelectedSite } from '../../../services/auth';
import { Roles } from '../../../models/roles';
import LargeDeliveriesSelector from '../../../components/LargeDeliveriesSelector';
import ButtonWithLoading from '../../../components/ButtonWithLoading';
import { fetchReceiversIfNeeded, updateReceiverCapacity } from '../../../actions/receivers';
import snackbarHelper from '../../../helpers/snackbarHelper';

const ReceiverMobileUpdateCapacityBreadcrumb = () => {
  const { formatMessage } = useIntl();

  return (
    <Box boxShadow={0}>
      <Breadcrumbs aria-label="breadcrumb">
        <StyledRescuerBreadcrumb
          component={Link}
          to={generatePath(routes.mobileDashboard)}
          label={formatMessage({ id: 'breadcrumbs.home' })}
          icon={<HomeIcon style={{ color: '#fff' }} fontSize="small" />}
        />
        <StyledRescuerTextBreadcrumb
          component="a"
          href="#"
          label={formatMessage({ id: 'breadcrumbs.receiver-update-capacity' })}
        />
      </Breadcrumbs>
    </Box>
  );
};

const ReceiverMobileUpdateCapacity = () => {
  const activeUser = useActiveUser();
  const dispatch = useDispatch();
  const userRoles = getUserRolesInCurrentlySelectedSite(activeUser);
  const rolesWithAccess = userRoles.filter(
    (role) => role.role_name === Roles.ReceiverLocationAdmin || role.role_name === Roles.ReceiverLocationStaff
  );
  const receiverId = rolesWithAccess[0]?.receiver_id;
  const receivers = useSelector((state) => state.entities.receivers);
  const locations = receivers.locations.byReceiverId?.[receiverId] || [];
  const receiverLocation = locations[0];
  const [isSubmitting, setIsSubmitting] = useState(false);

  useEffect(() => {
    dispatch(fetchReceiversIfNeeded());
  }, []);

  const formMethods = useForm({
    defaultValues: {
      large_deliveries: receiverLocation?.large_deliveries ? JSON.parse(receiverLocation.large_deliveries) : {},
    },
  });
  const {
    handleSubmit,
    reset,
    getValues,
    formState: { dirtyFields },
  } = formMethods;

  useEffect(() => {
    if (receiverLocation?.id) {
      reset({
        large_deliveries: receiverLocation?.large_deliveries ? JSON.parse(receiverLocation.large_deliveries) : {},
      });
    }
  }, [receiverLocation?.id]);

  const submitHandler = (data) => {
    dispatch(updateReceiverCapacity(receiverId, receiverLocation.id, data));
  };

  const onSubmit = (values) => {
    setIsSubmitting(true);

    const data = {
      ...receiverLocation,
      large_deliveries: values.large_deliveries,
    };

    return Bluebird.try(() => submitHandler(data))
      .then(() => {
        snackbarHelper.success('Capacity updated successfully!');
      })
      .catch((err) => {
        snackbarHelper.error(err.message);
      })
      .finally(() => {
        setIsSubmitting(false);
        reset(getValues(), { isSubmitted: true });
      });
  };

  return (
    <>
      <ReceiverMobileUpdateCapacityBreadcrumb />

      <Box px={1} py={1}>
        <FormContext {...formMethods}>
          <form onSubmit={handleSubmit(onSubmit)}>
            <LargeDeliveriesSelector />

            <Box py={1}>
              <ButtonWithLoading
                fullWidth
                disabled={!dirtyFields.size}
                type="submit"
                variant="contained"
                color="primary"
                isLoading={isSubmitting}
              >
                Save
              </ButtonWithLoading>
            </Box>
          </form>
        </FormContext>
      </Box>
    </>
  );
};

export default ReceiverMobileUpdateCapacity;
